class Overlay {
  private static instance: Overlay | null = null;
  private element: HTMLDivElement;
  private borderColor?: string;
  private fadeOutTimeout: NodeJS.Timeout | null = null;
  private isAnimating = false;
  private outsideCloseHandler: (() => void) | null = null;
  private backdropClickListener: ((event: MouseEvent) => void) | null = null;

  private constructor() {
    this.element = document.createElement('div');
    this.element.style.setProperty('position', 'fixed');
    this.element.style.setProperty('top', '0px');
    this.element.style.setProperty('left', '0px');
    this.element.style.setProperty('width', '100vw');
    this.element.style.setProperty('height', '100vh');
    this.element.style.setProperty('z-index', '2147483645');
    this.element.style.setProperty('pointer-events', 'none');
    this.element.style.setProperty('opacity', '0');
    this.element.style.setProperty('transition', 'opacity 0.5s ease-in-out');

    const backdropElement = document.createElement('div');
    backdropElement.style.setProperty('position', 'fixed');
    backdropElement.style.setProperty('top', '0px');
    backdropElement.style.setProperty('left', '0px');
    backdropElement.style.setProperty('width', '100vw');
    backdropElement.style.setProperty('height', '100vh');
    backdropElement.style.setProperty('background-color', 'rgba(0,0,0,0.5)');
    backdropElement.style.setProperty('z-index', '20');
    backdropElement.style.setProperty('pointer-events', 'none');
    backdropElement.style.setProperty('opacity', '0');
    backdropElement.style.setProperty('transition', 'opacity 0.5s ease-in-out');

    const borderElement = document.createElement('div');
    borderElement.style.setProperty('position', 'fixed');
    borderElement.style.setProperty('border', '3px solid #fff');
    borderElement.style.setProperty('box-sizing', 'border-box');
    borderElement.style.setProperty('z-index', '25');
    borderElement.style.setProperty('pointer-events', 'none');
    borderElement.style.setProperty('opacity', '0');
    borderElement.style.setProperty('transition', 'opacity 0.5s ease-in-out');

    const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svgElement.setAttribute('width', '100vw');
    svgElement.setAttribute('height', '100vh');
    svgElement.style.setProperty('position', 'fixed');
    svgElement.style.setProperty('top', '0');
    svgElement.style.setProperty('left', '0');
    svgElement.style.setProperty('width', '100vw');
    svgElement.style.setProperty('height', '100vh');
    svgElement.style.setProperty('z-index', '20');
    svgElement.style.setProperty('pointer-events', 'none');
    svgElement.style.setProperty('opacity', '0');
    svgElement.style.setProperty('transition', 'opacity 0.5s ease-in-out');

    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const mask = document.createElementNS('http://www.w3.org/2000/svg', 'mask');
    mask.setAttribute('id', 'overlay-mask');

    const whiteRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    whiteRect.setAttribute('x', '0');
    whiteRect.setAttribute('y', '0');
    whiteRect.setAttribute('width', '100vw');
    whiteRect.setAttribute('height', '100vh');
    whiteRect.setAttribute('fill', 'white');

    const blackRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    blackRect.setAttribute('x', '0');
    blackRect.setAttribute('y', '0');
    blackRect.setAttribute('width', '0');
    blackRect.setAttribute('height', '0');
    blackRect.setAttribute('fill', 'black');

    const backgroundRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    backgroundRect.setAttribute('x', '0');
    backgroundRect.setAttribute('y', '0');
    backgroundRect.setAttribute('width', '100vw');
    backgroundRect.setAttribute('height', '100vh');
    backgroundRect.setAttribute('fill', 'rgba(0,0,0,0.5)');
    backgroundRect.setAttribute('mask', 'url(#overlay-mask)');

    mask.appendChild(whiteRect);
    mask.appendChild(blackRect);
    defs.appendChild(mask);
    svgElement.appendChild(defs);
    svgElement.appendChild(backgroundRect);

    this.element.appendChild(backdropElement);
    this.element.appendChild(borderElement);
    this.element.appendChild(svgElement);

    (this.element as any).backdropElement = backdropElement;
    (this.element as any).borderElement = borderElement;
    (this.element as any).svgElement = svgElement;
    (this.element as any).maskRect = blackRect;

    document.documentElement.appendChild(this.element);
  }

  public static getInstance(): Overlay {
    if (!Overlay.instance) {
      Overlay.instance = new Overlay();
    }
    return Overlay.instance;
  }

  public setOutsideCloseHandler(handler: (() => void) | null): void {
    this.clearBackdropClickListener();
    this.outsideCloseHandler = handler;

    const backdropElement = (this.element as any).backdropElement as HTMLDivElement;
    if (handler) {
      this.element.style.setProperty('pointer-events', 'auto');
      backdropElement.style.setProperty('pointer-events', 'auto');
      this.backdropClickListener = () => {
        this.outsideCloseHandler?.();
      };
      backdropElement.addEventListener('click', this.backdropClickListener);
    } else {
      this.element.style.setProperty('pointer-events', 'none');
      backdropElement.style.setProperty('pointer-events', 'none');
    }
  }

  private clearBackdropClickListener(): void {
    const backdropElement = (this.element as any).backdropElement as HTMLDivElement | undefined;
    if (backdropElement && this.backdropClickListener) {
      backdropElement.removeEventListener('click', this.backdropClickListener);
      this.backdropClickListener = null;
    }
  }

  public show(
    dimensions?: { x: number; y: number; width: number; height: number },
    showBackdrop = true,
    showBorder = true
  ): void {
    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout);
      this.fadeOutTimeout = null;
    }

    this.element.style.setProperty('display', 'block');

    const hasValidDimensions = dimensions && dimensions.width > 0 && dimensions.height > 0;

    if (showBackdrop) {
      if (hasValidDimensions) {
        (this.element as any).svgElement.style.setProperty('display', 'block');
        (this.element as any).backdropElement.style.setProperty('display', 'none');

        (this.element as any).maskRect.setAttribute('x', `${dimensions.x - 10}`);
        (this.element as any).maskRect.setAttribute('y', `${dimensions.y - 10}`);
        (this.element as any).maskRect.setAttribute('width', `${dimensions.width + 20}`);
        (this.element as any).maskRect.setAttribute('height', `${dimensions.height + 20}`);
      } else {
        (this.element as any).svgElement.style.setProperty('display', 'none');
        (this.element as any).backdropElement.style.setProperty('display', 'block');
      }
    } else {
      (this.element as any).svgElement.style.setProperty('display', 'none');
      (this.element as any).backdropElement.style.setProperty('display', 'none');
    }

    if (hasValidDimensions && showBorder) {
      (this.element as any).borderElement.style.setProperty('display', 'block');
      (this.element as any).borderElement.style.setProperty('top', `${dimensions.y - 10}px`);
      (this.element as any).borderElement.style.setProperty('left', `${dimensions.x - 10}px`);
      (this.element as any).borderElement.style.setProperty('width', `${dimensions.width + 20}px`);
      (this.element as any).borderElement.style.setProperty(
        'height',
        `${dimensions.height + 20}px`
      );
      (this.element as any).borderElement.style.setProperty('border-color', this.borderColor);
    } else {
      (this.element as any).borderElement.style.setProperty('display', 'none');
    }

    if (!this.isAnimating) {
      this.isAnimating = true;

      this.element.style.setProperty('opacity', '0');
      if (showBorder) {
        (this.element as any).borderElement.style.setProperty('opacity', '0');
      }
      if (showBackdrop) {
        (this.element as any).svgElement.style.setProperty('opacity', '0');
        (this.element as any).backdropElement.style.setProperty('opacity', '0');
      }

      this.element.offsetHeight;

      this.element.style.setProperty('opacity', '1');
      if (hasValidDimensions && showBorder && (this.element as any).borderElement) {
        (this.element as any).borderElement.style.setProperty('opacity', '1');
      }
      if (showBackdrop) {
        if (hasValidDimensions && (this.element as any).svgElement) {
          (this.element as any).svgElement.style.setProperty('opacity', '1');
        }
        if (!hasValidDimensions && (this.element as any).backdropElement) {
          (this.element as any).backdropElement.style.setProperty('opacity', '1');
        }
      }

      setTimeout(() => {
        if (this) {
          this.isAnimating = false;
        }
      }, 500);
    } else {
      this.element.style.setProperty('opacity', '1');
      if (showBorder) {
        (this.element as any).borderElement.style.setProperty('opacity', '1');
      }
      if (showBackdrop) {
        (this.element as any).svgElement.style.setProperty('opacity', '1');
        (this.element as any).backdropElement.style.setProperty('opacity', '1');
      }
    }
  }

  public hide(): void {
    if (this) {
      if (!this.element) return;

      if (this.fadeOutTimeout) {
        clearTimeout(this.fadeOutTimeout);
      }

      this.setOutsideCloseHandler(null);

      this.element.style.setProperty('opacity', '0');
      if ((this.element as any).borderElement) {
        (this.element as any).borderElement.style.setProperty('opacity', '0');
      }
      if ((this.element as any).svgElement) {
        (this.element as any).svgElement.style.setProperty('opacity', '0');
      }
      if ((this.element as any).backdropElement) {
        (this.element as any).backdropElement.style.setProperty('opacity', '0');
      }

      this.fadeOutTimeout = setTimeout(() => {
        if (this.element) {
          this.element.style.setProperty('display', 'none');
        }
        if (this) {
          this.fadeOutTimeout = null;
        }
      }, 500);
    }
  }

  public destroy(): void {
    if (this) {
      if (this.fadeOutTimeout) {
        clearTimeout(this.fadeOutTimeout);
        this.fadeOutTimeout = null;
      }

      this.setOutsideCloseHandler(null);

      if (this.element) {
        document.documentElement.removeChild(this.element);
      }
      Overlay.instance = null;
    }
  }

  public setBorderColor(borderColor: string) {
    this.borderColor = borderColor;
  }
}

export default Overlay;
